"use client";

import * as React from "react";
import { Questionnaire as QuestionnairePrimitive } from "@shadcn/react/questionnaire";
import { cn } from "./utils";
import { CheckIcon } from "lucide-react";

import { buttonVariants, type Button } from "./button";
import { CHOICE_CONTROL_DOT_SIZE, CHOICE_CONTROL_SIZE, CHOICE_CONTROL_TICK_SIZE } from "./choice-control";

function Questionnaire({ className, ...props }: React.ComponentProps<typeof QuestionnairePrimitive.Root>) {
    return (
        <QuestionnairePrimitive.Root
            data-slot="questionnaire"
            className={cn("flex flex-col gap-4 w-full min-w-0", className)}
            {...props}
        />
    );
}

function QuestionnaireProgress({ className, ...props }: React.ComponentProps<typeof QuestionnairePrimitive.Progress>) {
    return (
        <QuestionnairePrimitive.Progress
            data-slot="questionnaire-progress"
            className={cn(
                "w-fit min-w-[14ch] min-h-[1lh] font-medium tabular-nums text-muted-foreground text-xs",
                className
            )}
            {...props}
        />
    );
}

function QuestionnaireItem({ className, ...props }: React.ComponentProps<typeof QuestionnairePrimitive.Item>) {
    return (
        <QuestionnairePrimitive.Item
            data-slot="questionnaire-item"
            className={cn("flex flex-col gap-4 p-0 border-0 outline-none min-w-0", className)}
            {...props}
        />
    );
}

function QuestionnaireTitle({ className, ...props }: React.ComponentProps<typeof QuestionnairePrimitive.Title>) {
    return (
        <QuestionnairePrimitive.Title
            data-slot="questionnaire-title"
            className={cn(
                "cn-font-heading text-base md:text-md leading-snug font-medium text-pretty [&:not(:has(~[data-slot=questionnaire-description]))]:mb-4",
                className
            )}
            {...props}
        />
    );
}

function QuestionnaireDescription({
    className,
    ...props
}: React.ComponentProps<typeof QuestionnairePrimitive.Description>) {
    return (
        <QuestionnairePrimitive.Description
            data-slot="questionnaire-description"
            className={cn("text-muted-foreground md:text-md text-sm text-pretty", className)}
            {...props}
        />
    );
}

function QuestionnaireChoices({ className, ...props }: React.ComponentProps<typeof QuestionnairePrimitive.Choices>) {
    return (
        <QuestionnairePrimitive.Choices
            data-slot="questionnaire-choices"
            className={cn("group/questionnaire-choices gap-2 grid min-w-0", className)}
            {...props}
        />
    );
}

// Tailwind 3 here, so state styling uses data-[...] and arbitrary variants, not the v4 shorthand.
// The primitive puts data-checked, data-type, data-disabled and data-invalid on the Choice.
function QuestionnaireChoice({
    children,
    className,
    inputProps,
    trailing,
    ...props
}: React.ComponentProps<typeof QuestionnairePrimitive.Choice> & {
    /** Extra props for the native radio or checkbox, for example a test id. */
    inputProps?: React.ComponentProps<typeof QuestionnairePrimitive.ChoiceInput>;
    /** A control at the end of the row, such as an edit button. Sits above the row's hit area. */
    trailing?: React.ReactNode;
}) {
    return (
        <QuestionnairePrimitive.Choice
            data-slot="questionnaire-choice"
            className={cn(
                "group/questionnaire-choice relative flex items-center gap-3 bg-white/70 data-[checked]:bg-indigo-50 hover:bg-indigo-50 dark:bg-slate-900/40 dark:data-[checked]:bg-indigo-950 px-3 py-2.5 border-2 border-slate-300 data-[checked]:border-indigo-600 data-[invalid]:border-red-500 rounded-lg outline-none has-[>input:focus-visible]:ring-2 has-[>input:focus-visible]:ring-indigo-600 min-h-12 md:text-md text-sm text-start transition-colors cursor-pointer select-none",
                "data-[disabled]:pointer-events-none data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50",
                // Indicator: a ring that fills when checked, round for radios, square for checkboxes.
                "[&[data-type=radio]_[data-slot=questionnaire-choice-indicator]]:rounded-full [&[data-checked]_[data-slot=questionnaire-choice-indicator]]:border-indigo-600 [&[data-checked]_[data-slot=questionnaire-choice-indicator]]:bg-indigo-600",
                "[&[data-checked][data-type=radio]_[data-slot=questionnaire-choice-indicator-dot]]:block [&[data-checked][data-type=checkbox]_[data-slot=questionnaire-choice-indicator-check]]:block [&[data-shortcut]_[data-slot=questionnaire-choice-shortcut]]:inline-flex",
                className
            )}
            {...props}
        >
            <QuestionnairePrimitive.ChoiceInput
                data-slot="questionnaire-choice-input"
                {...inputProps}
                className={cn("z-10 absolute inset-0 opacity-0 size-full cursor-pointer", inputProps?.className)}
            />
            <span
                aria-hidden="true"
                data-slot="questionnaire-choice-indicator"
                className={cn(
                    CHOICE_CONTROL_SIZE,
                    "relative flex justify-center items-center bg-white dark:bg-slate-900 border-2 border-slate-400 rounded-md text-white pointer-events-none shrink-0"
                )}
            >
                <span
                    data-slot="questionnaire-choice-indicator-dot"
                    className={cn("hidden bg-white rounded-full", CHOICE_CONTROL_DOT_SIZE)}
                />
                <CheckIcon
                    data-slot="questionnaire-choice-indicator-check"
                    strokeWidth={3}
                    className={cn("hidden", CHOICE_CONTROL_TICK_SIZE)}
                />
            </span>
            <QuestionnairePrimitive.ChoiceLabel
                data-slot="questionnaire-choice-label"
                className="flex flex-col flex-1 gap-0.5 min-w-0 leading-snug"
            >
                {children}
            </QuestionnairePrimitive.ChoiceLabel>
            <QuestionnairePrimitive.ChoiceShortcut
                data-slot="questionnaire-choice-shortcut"
                className="hidden justify-center items-center bg-white ms-auto border border-slate-300 rounded-md size-5 font-mono font-medium text-2xs text-slate-500 leading-none pointer-events-none shrink-0"
            />
            {trailing ? <span className="z-20 relative -my-2 shrink-0">{trailing}</span> : null}
        </QuestionnairePrimitive.Choice>
    );
}

function QuestionnaireChoiceDescription({ className, ...props }: React.ComponentProps<"span">) {
    return (
        <span
            data-slot="questionnaire-choice-description"
            className={cn("text-muted-foreground", className)}
            {...props}
        />
    );
}

function QuestionnaireInput({ className, ...props }: React.ComponentProps<typeof QuestionnairePrimitive.Input>) {
    return (
        <div data-slot="questionnaire-input-wrapper" className="group/questionnaire-input relative w-full min-w-0">
            <QuestionnairePrimitive.Input
                data-slot="questionnaire-input"
                className={cn(
                    "bg-transparent disabled:bg-input/50 dark:bg-input/30 dark:disabled:bg-input/80 disabled:opacity-50 px-2.5 py-1 border border-input aria-invalid:border-destructive focus-visible:border-ring dark:aria-invalid:border-destructive/50 rounded-lg outline-none aria-invalid:ring-3 aria-invalid:ring-destructive/20 focus-visible:ring-3 focus-visible:ring-ring/50 dark:aria-invalid:ring-destructive/40 w-full min-w-0 h-8 min-h-11 sm:min-h-0 md:text-sm text-base transition-[color,box-shadow,background-color] disabled:cursor-not-allowed disabled:pointer-events-none",
                    "selection:bg-primary selection:text-primary-foreground placeholder:text-muted-foreground",
                    className
                )}
                {...props}
            />
        </div>
    );
}

function QuestionnaireError({ className, ...props }: React.ComponentProps<typeof QuestionnairePrimitive.Error>) {
    return (
        <QuestionnairePrimitive.Error
            data-slot="questionnaire-error"
            className={cn("mt-2 text-destructive text-sm", className)}
            {...props}
        />
    );
}

function QuestionnaireActions({ className, ...props }: React.ComponentProps<"div">) {
    return (
        <div
            data-slot="questionnaire-actions"
            className={cn(
                "items-center gap-2 grid grid-cols-[minmax(0,1fr)_auto_auto] w-full min-h-11 sm:min-h-8",
                className
            )}
            {...props}
        />
    );
}

function QuestionnairePrevious({
    children,
    className,
    size = "default",
    variant = "outline",
    ...props
}: React.ComponentProps<typeof QuestionnairePrimitive.Previous> &
    Pick<React.ComponentProps<typeof Button>, "size" | "variant">) {
    return (
        <QuestionnairePrimitive.Previous
            data-slot="questionnaire-previous"
            data-size={size}
            data-variant={variant}
            className={cn(
                buttonVariants({ size, variant }),
                "col-start-1 row-start-1 min-h-11 justify-self-start sm:min-h-0",
                className
            )}
            {...props}
        >
            {children ?? "Previous"}
        </QuestionnairePrimitive.Previous>
    );
}

function QuestionnaireSkip({
    children,
    className,
    size = "default",
    variant = "outline",
    ...props
}: React.ComponentProps<typeof QuestionnairePrimitive.Skip> &
    Pick<React.ComponentProps<typeof Button>, "size" | "variant">) {
    return (
        <QuestionnairePrimitive.Skip
            data-slot="questionnaire-skip"
            data-size={size}
            data-variant={variant}
            className={cn(
                buttonVariants({ size, variant }),
                "col-start-2 row-start-1 min-h-11 justify-self-end sm:min-h-0",
                className
            )}
            {...props}
        >
            {children ?? "Skip"}
        </QuestionnairePrimitive.Skip>
    );
}

function QuestionnaireNext({
    children,
    className,
    size = "default",
    variant = "default",
    ...props
}: React.ComponentProps<typeof QuestionnairePrimitive.Next> &
    Pick<React.ComponentProps<typeof Button>, "size" | "variant">) {
    return (
        <QuestionnairePrimitive.Next
            data-slot="questionnaire-next"
            data-size={size}
            data-variant={variant}
            className={cn(
                buttonVariants({ size, variant }),
                "col-start-3 row-start-1 min-h-11 justify-self-end sm:min-h-0",
                className
            )}
            {...props}
        >
            {children ?? "Next"}
        </QuestionnairePrimitive.Next>
    );
}

function QuestionnaireSubmit({
    children,
    className,
    size = "default",
    variant = "default",
    ...props
}: React.ComponentProps<typeof QuestionnairePrimitive.Submit> &
    Pick<React.ComponentProps<typeof Button>, "size" | "variant">) {
    return (
        <QuestionnairePrimitive.Submit
            data-slot="questionnaire-submit"
            data-size={size}
            data-variant={variant}
            className={cn(
                buttonVariants({ size, variant }),
                "col-start-3 row-start-1 min-h-11 justify-self-end sm:min-h-0",
                className
            )}
            {...props}
        >
            {children ?? "Submit"}
        </QuestionnairePrimitive.Submit>
    );
}

export {
    Questionnaire,
    QuestionnaireActions,
    QuestionnaireChoice,
    QuestionnaireChoiceDescription,
    QuestionnaireChoices,
    QuestionnaireDescription,
    QuestionnaireError,
    QuestionnaireInput,
    QuestionnaireItem,
    QuestionnaireNext,
    QuestionnairePrevious,
    QuestionnaireProgress,
    QuestionnaireSkip,
    QuestionnaireSubmit,
    QuestionnaireTitle,
};

/** For controls drawn to sit in a choice list, such as "Other". */
export { CHOICE_CONTROL_SIZE };
